import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import type { Signature } from '@/types/signature';
import type { OrgOpSignableDecision } from '@mls-wallet/shared-types';

type CreateAdminChangeSignatureArg = {
  proposalId: string;
  decision: OrgOpSignableDecision;
};

const fetcher = async (
  _key: string,
  { arg }: { arg: CreateAdminChangeSignatureArg }
): Promise<Signature[]> => {
  return await denApi.post<Signature[], { decision: OrgOpSignableDecision }>(
    `admin/organization/admin-change-proposals/${encodeURIComponent(
      arg.proposalId
    )}/signatures`,
    { decision: arg.decision }
  );
};

/**
 * Signs (approves or rejects) an admin change proposal.
 * Each admin can only submit one signature per proposal.
 * The proposal requires a threshold number of approvals before it can be executed.
 *
 * @param swrOptions - Optional SWR mutation configuration overrides
 * @returns signProposal trigger function, loading state, and error
 */
export function useAdminChangeProposalSignature(
  swrOptions?: SWRMutationConfiguration<
    Signature[],
    Error,
    string,
    CreateAdminChangeSignatureArg
  >
) {
  const { trigger, isMutating, error } = useSWRMutation<
    Signature[],
    Error,
    string,
    CreateAdminChangeSignatureArg
  >(
    'admin/organization/admin-change-proposals/signatures',
    fetcher,
    swrOptions
  );

  return { signProposal: trigger, isLoading: isMutating, error };
}
